<!-- src/components/Notifications/AlertCard.vue -->
<template>
  <div
    class="flex items-start gap-3 rounded-lg border p-3 transition-colors"
    :class="
      mode === 'active'
        ? 'border-gray-200 bg-white hover:border-gray-300'
        : 'border-gray-100 bg-gray-50'
    "
  >
    <!-- Icon -->
    <div
      class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
      :class="iconBgClass"
    >
      <component :is="icon" class="h-4 w-4" :class="iconColorClass" />
    </div>

    <!-- Content -->
    <div class="min-w-0 flex-1">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <p class="truncate text-sm font-medium text-gray-900">
            {{ title }}
          </p>
          <p v-if="subtitle" class="mt-0.5 line-clamp-2 text-xs text-gray-500">
            {{ subtitle }}
          </p>
        </div>
        <span class="flex-shrink-0 whitespace-nowrap text-[10px] text-gray-400">
          {{ timeAgo }}
        </span>
      </div>

      <!-- Actor -->
      <p v-if="actorLabel" class="mt-1 text-[10px] text-gray-400">
        {{ actorLabel }}
      </p>

      <!-- Actions -->
      <div class="mt-2 flex items-center gap-2">
        <template v-if="mode === 'active'">
          <button
            class="text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
            @click="$emit('view', alert)"
          >
            View
          </button>
          <button
            v-if="alert.customerId"
            class="text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
            @click="$emit('notes', alert)"
          >
            Notes
          </button>
          <button
            class="text-[11px] text-gray-500 hover:text-gray-700 hover:underline"
            @click="$emit('dismiss', alert)"
          >
            Dismiss
          </button>
        </template>

        <template v-else>
          <button
            class="text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
            v-if="!(alert.kind === 'systemMonitoring' && alert.resolved)"
            @click="$emit('restore', alert)"
          >
            Restore
          </button>
          <button
            class="text-[11px] text-red-500 hover:text-red-700 hover:underline"
            @click="$emit('delete', alert)"
          >
            Delete
          </button>
          <span class="ml-auto text-[10px] text-gray-400">
            {{ alert.kind === 'systemMonitoring' && alert.resolved ? 'Resolved' : 'Dismissed' }}
            {{ dismissedTimeAgo }}
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useUsersStore } from '@/stores/usersStore'
import { useDataStore } from '@/stores/dataStore'
import {
  CheckCircle2,
  XCircle,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  AlertTriangle,
  Target,
  AtSign,
  StickyNote,
  Voicemail,
  PhoneMissed,
  Bell,
  Mail,
  MessageSquare,
  CalendarCheck,
  CalendarX,
  CalendarClock,
  Phone,
  Wrench,
  PackageX,
  MessageCircle,
} from 'lucide-vue-next'

const props = defineProps({
  alert: { type: Object, required: true },
  mode: { type: String, default: 'active' }, // 'active' or 'history'
})

defineEmits(['view', 'notes', 'dismiss', 'restore', 'delete'])

const usersStore = useUsersStore()
const dataStore = useDataStore()

// Resolve customer name: prefer alert.customerName, fall back to store lookup by customerId
const resolvedCustomerName = computed(() => {
  const a = props.alert
  if (a.customerName && a.customerName !== 'Customer') return a.customerName
  if (a.customerId) {
    const cust = dataStore.customersById[a.customerId]
    if (cust) {
      const first = cust.contact?.firstName || ''
      const last = cust.contact?.lastName || ''
      const full = `${first} ${last}`.trim()
      if (full) return full
    }
  }
  return a.customerName || 'Customer'
})

// Icon mapping
const iconConfig = computed(() => {
  const kind = props.alert.kind

  const configs = {
    customerCompleted: { icon: CheckCircle2, bg: 'bg-green-100', color: 'text-green-600' },
    customerCanceled: { icon: XCircle, bg: 'bg-red-100', color: 'text-red-600' },
    customerReactivated: { icon: RefreshCw, bg: 'bg-blue-100', color: 'text-blue-600' },
    customerOnHold: { icon: PauseCircle, bg: 'bg-amber-100', color: 'text-amber-600' },
    customerReleasedFromHold: { icon: PlayCircle, bg: 'bg-green-100', color: 'text-green-600' },
    taskRejected: { icon: AlertTriangle, bg: 'bg-red-100', color: 'text-red-600' },
    leadPool: { icon: Target, bg: 'bg-indigo-100', color: 'text-brand-blue' },
    noteTagged: { icon: AtSign, bg: 'bg-blue-100', color: 'text-blue-600' },
    noteAll: { icon: StickyNote, bg: 'bg-gray-100', color: 'text-gray-600' },
    voicemail: { icon: Voicemail, bg: 'bg-purple-100', color: 'text-purple-600' },
    unknownVoicemail: { icon: Voicemail, bg: 'bg-purple-100', color: 'text-purple-600' },
    unknownText: { icon: MessageSquare, bg: 'bg-amber-100', color: 'text-amber-600' },
    unknownEmail: { icon: Mail, bg: 'bg-amber-100', color: 'text-amber-600' },
    missedCall: { icon: PhoneMissed, bg: 'bg-orange-100', color: 'text-orange-600' },
    missedCallBrowser: { icon: PhoneMissed, bg: 'bg-orange-100', color: 'text-orange-600' },
    unreadText: { icon: MessageSquare, bg: 'bg-green-100', color: 'text-green-600' },
    unreadEmail: { icon: Mail, bg: 'bg-blue-100', color: 'text-blue-600' },
    unreadChat: { icon: MessageCircle, bg: 'bg-emerald-100', color: 'text-emerald-600' },
    emailAll: { icon: Mail, bg: 'bg-blue-100', color: 'text-blue-600' },
    textAll: { icon: MessageSquare, bg: 'bg-green-100', color: 'text-green-600' },
    // Calendar Events
    calendarEventAccepted: { icon: CalendarCheck, bg: 'bg-green-100', color: 'text-green-600' },
    calendarEventDeclined: { icon: CalendarX, bg: 'bg-red-100', color: 'text-red-600' },
    calendarEventTentative: { icon: CalendarClock, bg: 'bg-amber-100', color: 'text-amber-600' },
    communicationResolutionNeeded: {
      // Dynamic Icon based on commType, but keep Orange style for "Action Needed"
      icon: (() => {
        const t = props.alert.commType || props.alert.extra?.commType
        if (t === 'email') return Mail
        if (t === 'phone' || t === 'voicemail') return Phone
        return MessageSquare
      })(),
      bg: 'bg-orange-100',
      color: 'text-orange-600',
    },
    powerHubAlertAutoCreate: { icon: Wrench, bg: 'bg-amber-100', color: 'text-amber-600' },
    kittingProblem: { icon: PackageX, bg: 'bg-red-100', color: 'text-red-600' },
  }

  return configs[kind] || { icon: Bell, bg: 'bg-gray-100', color: 'text-gray-600' }
})

const icon = computed(() => iconConfig.value.icon)
const iconBgClass = computed(() => iconConfig.value.bg)
const iconColorClass = computed(() => iconConfig.value.color)

// Title
const title = computed(() => {
  const a = props.alert
  const name = resolvedCustomerName.value

  switch (a.kind) {
    case 'customerCompleted':
      return `${name} — Completed`
    case 'customerCanceled':
      return `${name} — Canceled`
    case 'customerReactivated':
      return `${name} — Reactivated`
    case 'customerOnHold':
      return `${name} — On Hold (${a.holdIsInternal ? 'Internal' : 'External'})`
    case 'customerReleasedFromHold':
      return `${name} — Released from Hold`
    case 'taskRejected':
      return `${name} — Task Rejected`
    case 'leadPool':
      return 'New Leads Available'
    case 'noteTagged':
      return `Tagged in Note — ${name}`
    case 'noteAll':
      return `New Note — ${name}`
    case 'voicemail':
      return `Voicemail — ${name}`
    case 'unknownVoicemail':
      return 'Voicemail — Unknown Caller'
    case 'unknownText':
      return a.fromNumber ? `Text — ${a.fromNumber}` : 'Text — Unknown Number'
    case 'unknownEmail':
      return a.fromEmail ? `Email — ${a.fromEmail}` : 'Email — Unknown Sender'
    case 'unreadText':
      return `New Text — ${name}`
    case 'unreadEmail':
      return `New Email — ${name}`
    case 'unreadChat':
      return `New Chat — ${name}`
    case 'missedCall':
    case 'missedCallBrowser':
      return `Missed Call — ${name}`
    case 'calendarEventAccepted':
      return `${a.attendeeName || 'Worker'} accepted calendar event`
    case 'calendarEventDeclined':
      return `${a.attendeeName || 'Worker'} declined calendar event`
    case 'calendarEventTentative':
      return `${a.attendeeName || 'Worker'} tentatively accepted`
    case 'communicationResolutionNeeded':
      return a.title || 'Response Needed'
    case 'powerHubAlertAutoCreate':
      return a.customerName || a.title || 'Service Case Auto-Created'
    case 'kittingProblem':
      return a.title || `${name} — Kitting Problem`
    case 'inventoryLowStock':
      return a.title || `Low Stock — ${a.catalogName || 'Equipment'}`
    case 'inventoryCritical':
      return a.title || `Critical Stock — ${a.catalogName || 'Equipment'}`
    default:
      return a.title || 'Alert'
  }
})

// Subtitle
const subtitle = computed(() => {
  const a = props.alert

  switch (a.kind) {
    case 'taskRejected':
      return `${a.taskDisplayName || 'A task'} was rejected. Review notes for details.`
    case 'leadPool':
      return 'New unclaimed leads have been added to the pool.'
    case 'unreadText':
    case 'unreadEmail':
    case 'unreadChat':
      // Backend passes message preview in subtitle field
      return a.subtitle || null
    case 'unknownText':
    case 'unknownEmail':
      // The text's first words, or the email's subject (receiveTextReply / fetchEmailReplies)
      return a.subtitle || a.preview || a.subject || null
    case 'calendarEventAccepted':
    case 'calendarEventDeclined':
    case 'calendarEventTentative':
      return buildCalendarSubtitle(a)
    case 'communicationResolutionNeeded': {
      // 1. Try to construct client-side for correct User Timezone
      const extra = a.extra || {}
      if (extra.commTimestamp) {
        // Handle Firestore Timestamp or Date object
        let ts
        if (extra.commTimestamp.toDate) {
          ts = extra.commTimestamp.toDate()
        } else if (extra.commTimestamp.seconds) {
          ts = new Date(extra.commTimestamp.seconds * 1000)
        } else {
          ts = new Date(extra.commTimestamp)
        }

        const timeStr = ts.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })

        const type =
          (extra.commType || 'Communication').charAt(0).toUpperCase() +
          (extra.commType || '').slice(1)
        const name = a.customerName || 'Customer'

        return `${type} from ${name} at ${timeStr} needs response`
      }

      // 2. Fallback to backend string
      if (a.subtitle) return a.subtitle

      // 3. Fallback safety
      const h = a.hoursElapsed || 0
      const hours = h === 1 ? '1 hour' : `${h} hours`
      return a.reason === 'manual'
        ? 'Manually flagged for resolution'
        : `No response sent for ${hours}`
    }
    case 'powerHubAlertAutoCreate':
      return a.subtitle || 'Service case automatically created from PowerHub alert'
    case 'voicemail':
    case 'unknownVoicemail': {
      const parts = []
      if (a.callerIdName) parts.push(a.callerIdName)
      if (a.fromNumber) parts.push(a.fromNumber)
      return parts.length > 0 ? parts.join(' – ') : 'New voicemail received'
    }
    case 'missedCallBrowser':
    case 'missedCall':
      return a.fromNumber ? `From ${a.fromNumber}` : 'Call was not answered'
    default:
      // Allow backend subtitle to pass through for any other type
      return a.subtitle || null
  }
})

// Build detailed calendar event subtitle
const buildCalendarSubtitle = a => {
  const parts = []

  if (a.eventSummary) {
    parts.push(`Event: ${a.eventSummary}`)
  }

  if (a.eventStart) {
    try {
      const date = new Date(a.eventStart)
      const formatted = date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
      parts.push(`When: ${formatted}`)
    } catch (e) {
      // Ignore date parsing errors
    }
  }

  if (a.customerName) {
    parts.push(`Customer: ${a.customerName}`)
  }

  return parts.length > 0 ? parts.join(' • ') : 'Calendar event response'
}

// Actor label
const actorLabel = computed(() => {
  const a = props.alert
  if (!a.actorUid && !a.actorEmail) return null

  if (a.actorUid) {
    const user = usersStore.users?.[a.actorUid]
    if (user?.displayName) return `by ${user.displayName}`
    if (user?.email) return `by ${user.email}`
  }

  if (a.actorEmail) return `by ${a.actorEmail}`
  return null
})

// Time formatting
const formatTimeAgo = date => {
  if (!date) return ''

  const now = new Date()
  const diff = now - date
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const timeAgo = computed(() => formatTimeAgo(props.alert.at))
const dismissedTimeAgo = computed(() => formatTimeAgo(props.alert.dismissedAt))
</script>
